"use client"

import * as React from "react"
import { z } from "zod"
import { useFieldArray, useForm, useWatch } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { useRouter } from "next/navigation"
import { useQueryClient } from "@tanstack/react-query"
import { Clock, GitCommit, Plus, Trash2 } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Separator } from "@/components/ui/separator"
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from "@/components/ui/sheet"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Checkbox } from "@/components/ui/checkbox"
import { Badge } from "@/components/ui/badge"
import { Switch } from "@/components/ui/switch"
import { createClient, type ClientOrg, updateClient } from "@/services/clients"
import { useToast } from "@/components/ui/use-toast"
import { api } from "@/lib/api"

const additionalContactSchema = z.object({
  name: z.string().trim().optional(),
  email: z
    .string()
    .trim()
    .optional()
    .refine((value) => {
      if (!value) return true
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
    }, "Valid email required"),
  phone: z.string().trim().optional(),
})

const schema = z.object({
  companyName: z.string().min(2, "Company name is required"),
  domain: z.string().trim().optional(),
  sectors: z.array(z.string()).optional(),

  primaryName: z.string().min(2, "Primary contact name is required"),
  primaryEmail: z.string().email("Valid email required"),
  primaryPhone: z.string().trim().optional(),

  additionalContacts: z.array(additionalContactSchema).optional(),

  accessStart: z.string().min(1, "Start date is required"),
  accessEnd: z.string().min(1, "End date is required"),
}).refine((data) => {
  if (!data.accessStart || !data.accessEnd) return true
  const start = new Date(data.accessStart)
  const end = new Date(data.accessEnd)
  return end >= start
}, { message: "End date must be after start date", path: ["accessEnd"] })

export type OnboardClientDialogProps = {
  open: boolean
  onOpenChangeAction: (open: boolean) => void
  client?: ClientOrg | null
}

export function OnboardClientDialog({ open, onOpenChangeAction, client }: OnboardClientDialogProps) {
  const router = useRouter()
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const [tab, setTab] = React.useState<"details" | "access" | "audit">("details")
  const [createdClientId, setCreatedClientId] = React.useState<number | null>(client?.id ?? null)
  const [auditNowTs, setAuditNowTs] = React.useState<number>(() => Date.now())

  const isEditMode = Boolean(client && client.id)

  const { control, register, handleSubmit, setValue, reset, formState: { errors, isSubmitting } } = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      domain: "",
      sectors: [],
      primaryPhone: "",
      additionalContacts: [],
    },
  })

  const { fields, append, remove } = useFieldArray({
    control,
    name: "additionalContacts",
  })

  async function onSubmit(values: z.infer<typeof schema>) {
    try {
      const payload = {
        name: values.companyName,
        domain: values.domain || undefined,
        sectors: (values.sectors || []).map((v) => v.trim().toLowerCase()).filter(Boolean),
      }

      if (isEditMode && client?.id) {
        const updated = await updateClient(client.id, payload)
        await queryClient.invalidateQueries({ queryKey: ["clients"] })
        toast({ title: "Client updated", description: "Changes have been saved." })
        setCreatedClientId(updated?.id ?? client.id)
        onOpenChangeAction(false)
        return
      }

      const created = await createClient(payload)
      await queryClient.invalidateQueries({ queryKey: ["clients"] })
      toast({ title: "Client created", description: "We saved the client. You can configure details next." })
      setCreatedClientId(created?.id ?? null)
      onOpenChangeAction(false)
      router.push("/projects/new")
    } catch (e: any) {
      toast({
        title: isEditMode ? "Failed to update client" : "Failed to create client",
        description: e?.response?.data?.detail || "Please try again.",
        variant: "destructive",
      })
    }
  }

  React.useEffect(() => {
    if (!open) {
      setTab("details")
      setCreatedClientId(client?.id ?? null)
      return
    }
    if (client?.id) {
      setCreatedClientId(client.id)
      reset({
        companyName: client.name,
        domain: client.domain || "",
        sectors: (client.sectors || []).map((s) => String(s).trim().toLowerCase()).filter(Boolean),
        primaryPhone: "",
        additionalContacts: [],
        primaryName: "",
        primaryEmail: "",
        accessStart: "",
        accessEnd: "",
      })
    }
    const id = window.setInterval(() => setAuditNowTs(Date.now()), 60_000)
    return () => window.clearInterval(id)
  }, [open, client, reset])

  const SECTOR_OPTIONS = React.useMemo<readonly string[]>(() => {
    return [
      'startup',
      'ai',
      'innovation',
      'manufacturing',
      'fintech',
      'healthcare',
      'pharmaceuticals',
      'energy',
      'retail',
      'ecommerce',
      'government',
      'education',
      'telecom',
      'saas',
      'cloud',
      'cybersecurity',
    ]
  }, [])

  const sectorsValue = (useWatch({ control, name: 'sectors' }) || []) as string[]

  const auditQuery = (() => {
    const enabled = Boolean(open && tab === 'audit' && createdClientId)
    const queryKey = ['client-audit-log', createdClientId]
    return { enabled, queryKey }
  })()

  const accessQuery = (() => {
    const enabled = Boolean(open && tab === 'access' && createdClientId)
    const queryKey = ['client-access-users', createdClientId]
    return { enabled, queryKey }
  })()

  const [auditItems, setAuditItems] = React.useState<readonly any[]>([])
  const [auditLoading, setAuditLoading] = React.useState<boolean>(false)
  const [auditError, setAuditError] = React.useState<string | null>(null)

  const [accessItems, setAccessItems] = React.useState<readonly any[]>([])
  const [accessLoading, setAccessLoading] = React.useState<boolean>(false)
  const [accessError, setAccessError] = React.useState<string | null>(null)

  const [portalEnabled, setPortalEnabled] = React.useState<boolean>(Boolean(client?.portal_access_enabled ?? true))
  const [portalLoading, setPortalLoading] = React.useState<boolean>(false)
  const [portalSaving, setPortalSaving] = React.useState<boolean>(false)

  React.useEffect(() => {
    if (!auditQuery.enabled || !createdClientId) return
    let cancelled = false
    setAuditLoading(true)
    setAuditError(null)
    api
      .get(`/api/projects/clients/${createdClientId}/audit-log/`, { params: { page: 1, page_size: 100 } })
      .then((res) => {
        if (cancelled) return
        const data = res.data as any
        const items = (data?.results || data?.items || data || []) as any[]
        setAuditItems(items)
      })
      .catch((e) => {
        if (cancelled) return
        setAuditError(e?.response?.data?.detail || e?.message || 'Failed to load audit log')
      })
      .finally(() => {
        if (cancelled) return
        setAuditLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [auditQuery.enabled, auditQuery.queryKey, createdClientId])

  React.useEffect(() => {
    if (!accessQuery.enabled || !createdClientId) return
    let cancelled = false
    setAccessLoading(true)
    setAccessError(null)
    api
      .get(`/api/projects/clients/${createdClientId}/access-users/`)
      .then((res) => {
        if (cancelled) return
        const data = res.data as any
        const items = (data?.results || data?.items || data || []) as any[]
        setAccessItems(items)
      })
      .catch((e) => {
        if (cancelled) return
        setAccessError(e?.response?.data?.detail || e?.message || 'Failed to load access users')
      })
      .finally(() => {
        if (cancelled) return
        setAccessLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [accessQuery.enabled, accessQuery.queryKey, createdClientId])

  React.useEffect(() => {
    if (!open || tab !== 'access' || !createdClientId) return
    let cancelled = false
    setPortalLoading(true)
    api
      .get(`/api/projects/clients/${createdClientId}/`)
      .then((res) => {
        if (cancelled) return
        const data = res.data as any
        setPortalEnabled(Boolean(data?.portal_access_enabled ?? true))
      })
      .catch(() => {
        if (cancelled) return
      })
      .finally(() => {
        if (cancelled) return
        setPortalLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [open, tab, createdClientId])

  function relTime(value: string): string {
    const created = new Date(value).getTime()
    const now = auditNowTs
    const diffSec = Math.max(0, Math.floor((now - created) / 1000))
    if (diffSec < 60) return `${diffSec}s ago`
    const diffMin = Math.floor(diffSec / 60)
    if (diffMin < 60) return `${diffMin}m ago`
    const diffHr = Math.floor(diffMin / 60)
    if (diffHr < 24) return `${diffHr}h ago`
    const diffDay = Math.floor(diffHr / 24)
    return `${diffDay}d ago`
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChangeAction}>
      <SheetContent side="right" className="sm:max-w-lg p-0 gap-0">
        <Tabs value={tab} onValueChange={(v) => setTab((v || 'details') as 'details' | 'access' | 'audit')} className="w-full h-full flex flex-col">
          <div className="relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-background to-background" />
            <div className="absolute -top-24 -right-24 h-64 w-64 rounded-full bg-gradient-to-br from-fuchsia-500/20 to-cyan-500/20 blur-2xl" />
            <div className="absolute -bottom-24 -left-24 h-64 w-64 rounded-full bg-gradient-to-br from-amber-500/20 to-indigo-500/20 blur-2xl" />
            <div className="relative pt-10" />
            <SheetHeader className="relative px-6 pb-4">
              <SheetTitle className="text-lg leading-tight truncate">Onboard Client</SheetTitle>
              <SheetDescription className="truncate">
                Capture company tags and contacts. You can refine later from Settings.
              </SheetDescription>
            </SheetHeader>
            <TabsList className="relative z-10 pointer-events-auto w-full grid grid-cols-3 rounded-none bg-transparent p-1">
              <TabsTrigger value="details" className="data-[state=active]:bg-background/80 data-[state=active]:backdrop-blur-sm rounded-md">Details</TabsTrigger>
              <TabsTrigger value="access" className="data-[state=active]:bg-background/80 data-[state=active]:backdrop-blur-sm rounded-md">Access</TabsTrigger>
              <TabsTrigger value="audit" className="data-[state=active]:bg-background/80 data-[state=active]:backdrop-blur-sm rounded-md">Audit log</TabsTrigger>
            </TabsList>
          </div>

          <div className="flex-1 overflow-y-auto">
            <TabsContent value="details" className="m-0">
              <Separator />
              <form onSubmit={handleSubmit(onSubmit)} className="px-6 py-5 space-y-6">
          <div className="space-y-3">
            <div className="space-y-4">
              <div className="grid grid-cols-[140px_1fr] items-center gap-3">
                <Label htmlFor="companyName" className="text-sm">Company</Label>
                <div className="space-y-2">
                  <Input id="companyName" placeholder="Acme Corp" {...register("companyName")} aria-invalid={!!errors.companyName} />
                  {errors.companyName && <p className="text-sm text-destructive">{errors.companyName.message}</p>}
                </div>
              </div>

              <div className="grid grid-cols-[140px_1fr] items-center gap-3">
                <Label htmlFor="domain" className="text-sm">Domain</Label>
                <Input id="domain" placeholder="acme.com" {...register("domain")} aria-invalid={!!errors.domain} />
              </div>

              <div className="grid grid-cols-[140px_1fr] items-start gap-3">
                <Label className="text-sm pt-2">Sectors</Label>
                <div className="space-y-2">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button type="button" variant="outline" className="w-full justify-between">
                        {sectorsValue.length ? 'Sectors selected' : 'Select sectors'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[320px]" align="start">
                      <div className="grid gap-2">
                        <div className="text-sm font-medium">Tag client sectors</div>
                        <div className="text-xs text-muted-foreground">Used for admin filtering and reporting.</div>
                        <Separator className="my-1" />
                        <div className="max-h-[260px] overflow-auto space-y-2">
                          {SECTOR_OPTIONS.map((opt) => {
                            const checked = sectorsValue.includes(opt)
                            return (
                              <div key={opt} className="flex items-center gap-2">
                                <Checkbox
                                  id={`sector-${opt}`}
                                  checked={checked}
                                  onCheckedChange={(next) => {
                                    const cur = sectorsValue
                                    const updated = next ? [...cur, opt] : cur.filter((v) => v !== opt)
                                    setValue('sectors', updated, { shouldDirty: true, shouldValidate: true })
                                  }}
                                />
                                <Label htmlFor={`sector-${opt}`} className="capitalize">{opt}</Label>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                  {sectorsValue.length ? (
                    <div className="flex flex-wrap gap-2">
                      {sectorsValue
                        .slice()
                        .sort((a, b) => a.localeCompare(b))
                        .map((s) => (
                          <Badge key={s} variant="secondary" className="capitalize">
                            {s}
                          </Badge>
                        ))}
                    </div>
                  ) : (
                    <div className="text-xs text-muted-foreground">Examples: startup, AI, innovation, manufacturing.</div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <h3 className="text-sm font-medium">Main point of contact</h3>
            <div className="grid grid-cols-1 gap-4">
              <div className="space-y-2">
                <Label htmlFor="primaryName">Full name</Label>
                <Input id="primaryName" placeholder="Jane Doe" {...register("primaryName")} aria-invalid={!!errors.primaryName} />
                {errors.primaryName && <p className="text-sm text-destructive">{errors.primaryName.message}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="primaryEmail">Their email address</Label>
                <Input id="primaryEmail" type="email" placeholder="jane@acme.com" {...register("primaryEmail")} aria-invalid={!!errors.primaryEmail} />
                {errors.primaryEmail && <p className="text-sm text-destructive">{errors.primaryEmail.message}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="primaryPhone">Contact number</Label>
                <Input id="primaryPhone" placeholder="+1 555 123 4567" {...register("primaryPhone")} aria-invalid={!!errors.primaryPhone} />
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-sm font-medium">Additional contacts</h3>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => append({ name: "", email: "", phone: "" })}
              >
                <Plus className="mr-2 h-4 w-4" />
                Add contact
              </Button>
            </div>

            {fields.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Add secondary or additional contacts if needed.
              </p>
            ) : (
              <div className="space-y-4">
                {fields.map((field, index) => {
                  const nameError = errors.additionalContacts?.[index]?.name
                  const emailError = errors.additionalContacts?.[index]?.email
                  const phoneError = errors.additionalContacts?.[index]?.phone

                  return (
                    <div key={field.id} className="rounded-md border bg-card p-4 space-y-4">
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-sm font-medium">Contact #{index + 1}</div>
                        <Button type="button" variant="ghost" size="icon" onClick={() => remove(index)} aria-label="Remove contact">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>

                      <div className="grid grid-cols-1 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor={`additionalContacts.${index}.name`}>Full name</Label>
                          <Input
                            id={`additionalContacts.${index}.name`}
                            placeholder="John Smith"
                            {...register(`additionalContacts.${index}.name`)}
                            aria-invalid={!!nameError}
                          />
                          {nameError && <p className="text-sm text-destructive">{String(nameError.message || '')}</p>}
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor={`additionalContacts.${index}.email`}>Email address</Label>
                          <Input
                            id={`additionalContacts.${index}.email`}
                            type="email"
                            placeholder="john@acme.com"
                            {...register(`additionalContacts.${index}.email`)}
                            aria-invalid={!!emailError}
                          />
                          {emailError && <p className="text-sm text-destructive">{String(emailError.message || '')}</p>}
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor={`additionalContacts.${index}.phone`}>Contact number</Label>
                          <Input
                            id={`additionalContacts.${index}.phone`}
                            placeholder="+1 555 987 6543"
                            {...register(`additionalContacts.${index}.phone`)}
                            aria-invalid={!!phoneError}
                          />
                          {phoneError && <p className="text-sm text-destructive">{String(phoneError.message || '')}</p>}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          <div className="space-y-3">
            <h3 className="text-sm font-medium">Length of Portal access</h3>
            <div className="grid grid-cols-1 gap-4">
              <div className="space-y-2">
                <Label htmlFor="accessStart">Start date</Label>
                <Input id="accessStart" type="date" {...register("accessStart")} aria-invalid={!!errors.accessStart} />
                {errors.accessStart && <p className="text-sm text-destructive">{errors.accessStart.message}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="accessEnd">End date</Label>
                <Input id="accessEnd" type="date" {...register("accessEnd")} aria-invalid={!!errors.accessEnd} />
                {errors.accessEnd && <p className="text-sm text-destructive">{errors.accessEnd.message}</p>}
              </div>
            </div>
          </div>

          <SheetFooter className="gap-2 border-t pt-4">
            <div className="flex w-full items-center justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChangeAction(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? (isEditMode ? "Saving…" : "Creating…") : (isEditMode ? "Save changes" : "Create")}
              </Button>
            </div>
          </SheetFooter>
              </form>
            </TabsContent>

            <TabsContent value="access" className="m-0">
              <Separator />
              <div className="px-6 py-5 space-y-4">
                <div className="flex items-start justify-between gap-4 rounded-md border bg-card p-4">
                  <div className="min-w-0">
                    <div className="text-sm font-medium">Client portal access</div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Disable to block client-side users from signing in. Cognisys internal staff keep access via projects.
                    </div>
                  </div>
                  <Switch
                    checked={portalEnabled}
                    disabled={!createdClientId || portalLoading || portalSaving}
                    onCheckedChange={async (next) => {
                      if (!createdClientId) return
                      setPortalEnabled(next)
                      setPortalSaving(true)
                      try {
                        await updateClient(createdClientId, { portal_access_enabled: next })
                        await queryClient.invalidateQueries({ queryKey: ["clients"] })
                        toast({ title: "Portal access updated", description: next ? "Client portal access enabled." : "Client portal access disabled." })
                      } catch (e: any) {
                        setPortalEnabled((prev) => !prev)
                        toast({
                          title: "Failed to update portal access",
                          description: e?.response?.data?.detail || "Please try again.",
                          variant: "destructive",
                        })
                      } finally {
                        setPortalSaving(false)
                      }
                    }}
                  />
                </div>

                {!createdClientId ? (
                  <div className="text-sm text-muted-foreground">Create the client first to view access.</div>
                ) : accessLoading ? (
                  <div className="text-sm text-muted-foreground">Loading…</div>
                ) : accessError ? (
                  <div className="text-sm text-muted-foreground">{accessError}</div>
                ) : accessItems.length === 0 ? (
                  <div className="text-sm text-muted-foreground">No users found.</div>
                ) : (
                  <div className="rounded-md border bg-card">
                    <div className="max-h-[520px] overflow-auto">
                      {accessItems.map((u: any) => {
                        const name = `${String(u.first_name || '').trim()} ${String(u.last_name || '').trim()}`.trim()
                        const label = name || String(u.email || '—')
                        const priority = { client: 0, explicit: 1, project: 2 } as const
                        const sources = (() => {
                          if (Array.isArray(u.sources)) return u.sources.map((s: any) => String(s)).filter(Boolean)
                          if (u.source) return [String(u.source)]
                          return []
                        })().sort((a: string, b: string) => {
                          return (priority[a as keyof typeof priority] ?? 99) - (priority[b as keyof typeof priority] ?? 99)
                        })
                        return (
                          <div key={u.id} className="flex items-center justify-between gap-3 border-b last:border-b-0 px-4 py-3">
                            <div className="min-w-0">
                              <div className="text-sm font-medium truncate">{label}</div>
                              <div className="text-xs text-muted-foreground truncate">{String(u.email || '—')}</div>
                            </div>
                            <div className="flex items-center gap-2">
                              {sources.map((s: string) => (
                                <Badge key={s} variant="secondary">{s}</Badge>
                              ))}
                              {u.is_superuser ? <Badge>Super Admin</Badge> : null}
                              {u.staff_role ? <Badge variant="outline">{String(u.staff_role)}</Badge> : null}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="audit" className="m-0">
              <Separator />
              <div className="px-6 py-5 space-y-4">
              {!createdClientId ? (
                <div className="text-sm text-muted-foreground">
                  Create the client first to view its audit log.
                </div>
              ) : auditLoading ? (
                <div className="text-sm text-muted-foreground">Loading…</div>
              ) : auditError ? (
                <div className="text-sm text-muted-foreground">{auditError}</div>
              ) : auditItems.length === 0 ? (
                <div className="text-sm text-muted-foreground">No entries yet.</div>
              ) : (
                <ol className="relative border-l-2 border-border ml-3 space-y-3">
                  {auditItems.map((it: any) => (
                    <li key={it.id} className="ml-4">
                      <div className="absolute -left-2.5 mt-1 flex h-5 w-5 items-center justify-center rounded-full bg-card border border-border">
                        <GitCommit className="w-4 h-4 text-zinc-600" />
                      </div>
                      <div className="flex items-start justify-between gap-3">
                        <div className="text-sm text-foreground">
                          <span className="font-medium">{String(it.action || 'event')}</span>
                          {it.actor_email ? <span className="text-muted-foreground">{' '}by {String(it.actor_email)}</span> : null}
                          {it.target_email ? <span className="text-muted-foreground">{' '}to {String(it.target_email)}</span> : null}
                        </div>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground whitespace-nowrap">
                          <Clock className="w-3.5 h-3.5" />
                          <span>{it.created_at ? relTime(String(it.created_at)) : '—'}</span>
                        </div>
                      </div>
                    </li>
                  ))}
                </ol>
              )}
              </div>
            </TabsContent>
          </div>
        </Tabs>
      </SheetContent>
    </Sheet>
  )
}
